import { HttpContext, httpResource } from '@angular/common/http';
import { Service, computed } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY } from '../../../core/http/http-context-tokens';

const RECURSO = `${environment.apiUrl}/sai/cartaservico/publico/destaque-rapido`;

/** O erro fica local: sem a resposta, a seção mostra o padrão do sistema, sem modal. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

/** Um destaque de `GET publico/destaque-rapido`. O destino vem cru, como o adm gravou. */
export interface DestaqueRapidoPublicoResponse {
  codDestaqueRapidoDrp: number;
  desTituloDrp: string;
  desDescricaoDrp: string | null;
  /** Nome do ícone Lucide; nulo quando o ícone é um arquivo enviado. */
  desIconeDrp: string | null;
  /** Versão do arquivo do ícone, para o `?v=`; nula no ícone da biblioteca. */
  desVersaoIconeDrp: string | null;
  desCorCardDrp: string | null;
  desTipoDestinoDrp: string;
  desDestinoDrp: string;
}

/** A resposta de `GET publico/destaque-rapido`. */
export interface DestaquesRapidosPublicosResponse {
  /** Falso: o portal publica o padrão dele, e `destaques` vem vazio. */
  bitPersonalizado: boolean;
  destaques: DestaqueRapidoPublicoResponse[];
}

/** Um destaque personalizado pronto para a seção, ainda com o destino a traduzir. */
export interface DestaqueRapidoPublico {
  id: number;
  titulo: string;
  descricao: string;
  /** Nome do ícone Lucide, ou `null` quando o ícone é a `imagem`. */
  icone: string | null;
  /** URL pública do ícone enviado, com a versão; `null` no ícone da biblioteca. */
  imagem: string | null;
  cor: string | null;
  tipoDestino: string;
  destino: string;
}

function textoOuNulo(valor: string | null | undefined): string | null {
  const limpo = valor?.trim();

  return limpo ? limpo : null;
}

/**
 * Os destaques da API prontos para a seção. A versão vai em `?v=`: a URL do ícone é fixa e tem
 * cache de 1 hora, e sem ela quem já tinha o ícone antigo continuaria com ele depois de uma
 * troca no adm. Por defesa, sai o destaque sem título e o que não tem ícone de nenhuma das
 * duas naturezas.
 */
export function paraDestaquesPublicos(
  resposta: readonly DestaqueRapidoPublicoResponse[]
): DestaqueRapidoPublico[] {
  return resposta
    .map((item) => {
      const versao = textoOuNulo(item.desVersaoIconeDrp);
      const imagem = versao
        ? `${RECURSO}/${item.codDestaqueRapidoDrp}/icone?v=${encodeURIComponent(versao)}`
        : null;
      const icone = imagem ? null : textoOuNulo(item.desIconeDrp);

      return {
        id: item.codDestaqueRapidoDrp,
        titulo: textoOuNulo(item.desTituloDrp) ?? '',
        descricao: textoOuNulo(item.desDescricaoDrp) ?? '',
        icone,
        imagem,
        cor: textoOuNulo(item.desCorCardDrp),
        tipoDestino: item.desTipoDestinoDrp ?? '',
        destino: item.desDestinoDrp ?? '',
      };
    })
    .filter((destaque) => destaque.titulo && (destaque.icone || destaque.imagem));
}

/**
 * Os destaques da seção Acesso rápido da homepage. É raiz: uma requisição por carregamento do
 * portal. O portal não envia o órgão: o back-end o resolve pelo host.
 *
 * `personalizado` falso — origem padrão, órgão sem escolha gravada, host não mapeado ou erro —
 * quer dizer "mostre o padrão do sistema". Verdadeiro com `destaques` vazio quer dizer "nenhum
 * destaque" (decisão da usuária de 26/10/2026). Durante a carga, a seção espera por
 * `carregando` para não trocar o padrão pelo personalizado na frente do visitante.
 */
@Service()
export class AcessoRapidoPublicoService {
  private readonly destaquesApi = httpResource<ResponseApi<DestaquesRapidosPublicosResponse>>(
    () => ({
      url: RECURSO,
      context: ERRO_LOCAL(),
    })
  );

  readonly carregando = computed(() => this.destaquesApi.isLoading());

  /** O `error()` é consultado antes do `value()`, que lança em estado de erro. */
  private readonly resposta = computed<DestaquesRapidosPublicosResponse | null>(() => {
    if (this.destaquesApi.error() !== undefined) return null;

    return this.destaquesApi.value()?.data ?? null;
  });

  readonly personalizado = computed(() => this.resposta()?.bitPersonalizado === true);

  readonly destaques = computed<readonly DestaqueRapidoPublico[]>(() =>
    this.personalizado() ? paraDestaquesPublicos(this.resposta()?.destaques ?? []) : []
  );
}
